import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ContentManager from './ContentManager';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ClipboardList, Mail, CalendarDays, Clock, ArrowLeft, RefreshCw, User, Phone, MapPin, LogOut, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BOOKING_STATUSES = [
  { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  { value: 'confirmed', label: 'Confirmed', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-purple-100 text-purple-800 border-purple-200' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800 border-green-200' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800 border-red-200' },
];
const CONTACT_STATUSES = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  { value: 'read', label: 'Read', color: 'bg-gray-100 text-gray-700 border-gray-200' },
  { value: 'replied', label: 'Replied', color: 'bg-green-100 text-green-800 border-green-200' },
  { value: 'archived', label: 'Archived', color: 'bg-gray-100 text-gray-500 border-gray-200' },
];

function getStatusBadge(status, statusList) {
  const s = statusList.find((x) => x.value === status) || statusList[0];
  return <Badge className={`${s.color} border font-medium`}>{s.label}</Badge>;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, loading: authLoading, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) navigate('/admin/login');
  }, [user, authLoading, navigate]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, b, c] = await Promise.all([
        fetch(`${API}/stats`, { credentials: 'include' }),
        fetch(`${API}/bookings`, { credentials: 'include' }),
        fetch(`${API}/contact`, { credentials: 'include' }),
      ]);
      setStats(await s.json());
      setBookings(await b.json());
      setContacts(await c.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user) fetchAll(); }, [user, fetchAll]);

  const updateBookingStatus = async (id, newStatus) => {
    await fetch(`${API}/bookings/${id}/status`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: newStatus }) });
    setBookings((prev) => prev.map((b) => b.id === id ? { ...b, status: newStatus } : b));
    const r = await fetch(`${API}/stats`, { credentials: 'include' });
    setStats(await r.json());
  };

  const updateContactStatus = async (id, newStatus) => {
    await fetch(`${API}/contact/${id}/status`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: newStatus }) });
    setContacts((prev) => prev.map((c) => c.id === id ? { ...c, status: newStatus } : c));
    const r = await fetch(`${API}/stats`, { credentials: 'include' });
    setStats(await r.json());
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  if (authLoading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-green-700" /></div>;
  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="gap-2 text-gray-600" data-testid="admin-back-btn">
              <ArrowLeft className="h-4 w-4" /> Back to Site
            </Button>
            <div className="h-6 w-px bg-gray-300" />
            <h1 className="text-xl font-bold text-gray-900" data-testid="admin-title">Admin Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 hidden sm:inline">{user.email}</span>
            <Button variant="outline" size="sm" onClick={fetchAll} className="gap-2" data-testid="admin-refresh-btn">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="gap-2 text-red-600 hover:text-red-700" data-testid="admin-logout-btn">
              <LogOut className="h-4 w-4" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats-grid">
            <StatCard icon={ClipboardList} label="Total Bookings" value={stats.total_bookings} accent="green" />
            <StatCard icon={Clock} label="Pending" value={stats.pending_bookings} accent="yellow" />
            <StatCard icon={Mail} label="Total Contacts" value={stats.total_contacts} accent="blue" />
            <StatCard icon={CalendarDays} label="Today" value={stats.today_bookings + stats.today_contacts} accent="purple" />
          </div>
        )}

        <Tabs defaultValue="bookings" className="space-y-4">
          <TabsList className="bg-white border border-gray-200 p-1 h-auto">
            <TabsTrigger value="bookings" className="px-4 py-2 data-[state=active]:bg-green-50 data-[state=active]:text-green-800" data-testid="tab-bookings">
              Quote Requests ({bookings.length})
            </TabsTrigger>
            <TabsTrigger value="contacts" className="px-4 py-2 data-[state=active]:bg-green-50 data-[state=active]:text-green-800" data-testid="tab-contacts">
              Contact Messages ({contacts.length})
            </TabsTrigger>
            <TabsTrigger value="content" className="px-4 py-2 data-[state=active]:bg-green-50 data-[state=active]:text-green-800" data-testid="tab-content">
              Manage Content
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings">
            <Card className="border border-gray-200" data-testid="bookings-table-card">
              <CardHeader className="pb-3"><CardTitle className="text-lg">Quote Requests</CardTitle></CardHeader>
              <CardContent className="p-0">
                {bookings.length === 0 ? (
                  <div className="text-center py-12 text-gray-500" data-testid="bookings-empty">No bookings yet.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="font-semibold">Customer</TableHead>
                          <TableHead className="font-semibold">Contact</TableHead>
                          <TableHead className="font-semibold">Address</TableHead>
                          <TableHead className="font-semibold">Date / Time</TableHead>
                          <TableHead className="font-semibold">Status</TableHead>
                          <TableHead className="font-semibold">Submitted</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bookings.map((b) => (
                          <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4 text-gray-400 shrink-0" />
                                <div><p className="font-medium text-gray-900">{b.name}</p><p className="text-xs text-gray-500">{b.property_type}</p></div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <p className="text-sm">{b.email}</p>
                              <div className="flex items-center gap-1 text-xs text-gray-500"><Phone className="h-3 w-3" />{b.phone}</div>
                            </TableCell>
                            <TableCell><div className="flex items-center gap-1 text-sm"><MapPin className="h-3 w-3 text-gray-400 shrink-0" /><span className="truncate max-w-[180px]">{b.address}</span></div></TableCell>
                            <TableCell><p className="text-sm">{b.preferred_date}</p><p className="text-xs text-gray-500">{b.time_slot}</p></TableCell>
                            <TableCell>
                              <Select value={b.status} onValueChange={(v) => updateBookingStatus(b.id, v)}>
                                <SelectTrigger className="w-[130px] h-8 text-xs" data-testid={`booking-status-${b.id}`}><SelectValue>{getStatusBadge(b.status, BOOKING_STATUSES)}</SelectValue></SelectTrigger>
                                <SelectContent>{BOOKING_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell className="text-xs text-gray-500">{formatDate(b.created_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="contacts">
            <Card className="border border-gray-200" data-testid="contacts-table-card">
              <CardHeader className="pb-3"><CardTitle className="text-lg">Contact Messages</CardTitle></CardHeader>
              <CardContent className="p-0">
                {contacts.length === 0 ? (
                  <div className="text-center py-12 text-gray-500" data-testid="contacts-empty">No messages yet.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="font-semibold">Name</TableHead>
                          <TableHead className="font-semibold">Contact</TableHead>
                          <TableHead className="font-semibold">Service Interest</TableHead>
                          <TableHead className="font-semibold">Message</TableHead>
                          <TableHead className="font-semibold">Status</TableHead>
                          <TableHead className="font-semibold">Submitted</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {contacts.map((c) => (
                          <TableRow key={c.id} data-testid={`contact-row-${c.id}`}>
                            <TableCell><div className="flex items-center gap-2"><User className="h-4 w-4 text-gray-400 shrink-0" /><p className="font-medium text-gray-900">{c.name}</p></div></TableCell>
                            <TableCell><p className="text-sm">{c.email}</p><div className="flex items-center gap-1 text-xs text-gray-500"><Phone className="h-3 w-3" />{c.phone}</div></TableCell>
                            <TableCell><Badge variant="secondary" className="bg-green-50 text-green-700 border border-green-200">{c.service_interest}</Badge></TableCell>
                            <TableCell><p className="text-sm text-gray-700 truncate max-w-[250px]">{c.message || '—'}</p></TableCell>
                            <TableCell>
                              <Select value={c.status} onValueChange={(v) => updateContactStatus(c.id, v)}>
                                <SelectTrigger className="w-[120px] h-8 text-xs" data-testid={`contact-status-${c.id}`}><SelectValue>{getStatusBadge(c.status, CONTACT_STATUSES)}</SelectValue></SelectTrigger>
                                <SelectContent>{CONTACT_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell className="text-xs text-gray-500">{formatDate(c.created_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="content">
            <ContentManager />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  const colors = { green: 'bg-green-50 text-green-700 border-green-200', yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200', blue: 'bg-blue-50 text-blue-700 border-blue-200', purple: 'bg-purple-50 text-purple-700 border-purple-200' };
  const iconColors = { green: 'bg-green-100 text-green-700', yellow: 'bg-yellow-100 text-yellow-700', blue: 'bg-blue-100 text-blue-700', purple: 'bg-purple-100 text-purple-700' };
  return (
    <Card className={`border ${colors[accent]}`} data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`p-2.5 rounded-lg ${iconColors[accent]}`}><Icon className="h-5 w-5" /></div>
        <div><p className="text-2xl font-bold">{value}</p><p className="text-xs font-medium opacity-80">{label}</p></div>
      </CardContent>
    </Card>
  );
}
